import {showNotification, Wizard} from 'platform/components';

import {
  BaseVehicle,
  useVehicleFormContext,
  useWizardTabRouting,
  useFormKeyboardNavigation,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {FullScreenModal} from '@dms/shared';

import {getSafely} from 'shared/web';

import {ServiceBasicInformation} from '../(sections)/ServiceBasicInformation';
import {ServiceFeatures} from '../(sections)/ServiceFeatures';
import {ServiceIntervals} from '../(sections)/ServiceIntervals';
import {ServiceVehicleData} from '../(sections)/ServiceVehicleData';
import {useServiceVehicleWizard} from '../hooks/useServiceVehicleWizard';

// Module-level: useWizardTabRouting keys its URL-sync effect on this identity.
const TAB_STEP_MAP = {
  'basic-information': 0,
  'vehicle-data': 1,
  features: 2,
  'service-intervals': 3,
};

interface ModalKeys {
  headline?: string;
  create?: string;
}

interface ServiceVehicleFormProps {
  isCreating: boolean;
  onSave: (values: BaseVehicle) => Promise<void>;
  onDiscard: () => void;
  tabRoutePattern?: keyof typeof vehiclesRoutes;
  modalKeys?: ModalKeys;
  disableVinDecoder?: boolean;
  vehicleId?: string;
  // Edit can only persist the vehicle class when a service vehicle exists; create always can.
  shouldShowVehicleClass?: boolean;
  // Warranties collected in the form are posted only by the create and clone flows; an existing
  // vehicle manages them on its detail page.
  shouldShowWarranties?: boolean;
  // Mounted outside a /vehicles route. Wizard steps then move without touching the URL, which
  // would otherwise navigate the host page away and discard its state.
  isEmbedded?: boolean;
  infoAlert?: {
    title: string;
    message: string;
  };
}

export const ServiceVehicleForm = ({
  isCreating,
  onSave,
  onDiscard,
  tabRoutePattern = 'createServiceVehicleV2Tab',
  modalKeys = {},
  disableVinDecoder = false,
  vehicleId,
  shouldShowVehicleClass = true,
  shouldShowWarranties = false,
  isEmbedded = false,
  infoAlert,
}: ServiceVehicleFormProps) => {
  const {control, formApi} = useVehicleFormContext();

  const {wizardRef, onStepChange} = useWizardTabRouting({
    tabStepMap: TAB_STEP_MAP,
    tabRoutePattern: isEmbedded ? undefined : getSafely(vehiclesRoutes, tabRoutePattern),
    defaultTab: 'basic-information',
  });

  const {onKeyDownCapture} = useFormKeyboardNavigation({
    wizardRef,
    onStepChange,
  });

  const wizardSteps = useServiceVehicleWizard(control, {
    basicInformation: (
      <ServiceBasicInformation
        disableVinDecoder={disableVinDecoder}
        vehicleId={vehicleId}
        shouldShowVehicleClass={shouldShowVehicleClass}
        isEmbedded={isEmbedded}
        infoAlert={infoAlert}
      />
    ),
    vehicleData: <ServiceVehicleData shouldShowWarranties={shouldShowWarranties} />,
    features: <ServiceFeatures />,
    serviceIntervals: <ServiceIntervals />,
  });

  return (
    <FullScreenModal
      data-testid="serviceVehicleForm"
      headline={modalKeys.headline ?? i18n.t('entity.vehicle.labels.createServiceVehicle')}
      backgroundColor="general.white"
      scrollable={false}
      actions={[
        {
          title: i18n.t('general.labels.discard'),
          variant: 'secondary',
          isLoading: isCreating,
          onClick: onDiscard,
        },
        {
          title: modalKeys.create ?? i18n.t('general.actions.create'),
          isLoading: isCreating,
          onClick: () => {
            formApi.handleSubmit(
              (values) => {
                onSave(values);
              },
              (error) => {
                if (i18n.exists('errors.invalid_submit')) {
                  showNotification.warning(i18n.t('errors.invalid_submit'));
                }

                if (process.env.ENV_TYPE === 'dev') {
                  console.error(error);
                }
              }
            )();
          },
        },
      ]}
    >
      <Wizard
        ref={wizardRef}
        orientation="horizontal"
        steps={wizardSteps}
        height="100%"
        onStepChange={onStepChange}
        onKeyDownCapture={onKeyDownCapture}
      />
    </FullScreenModal>
  );
};
